import React from 'react';
import { Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-bootstrap';
import { useSubscription } from '../context/SubscriptionContext';
import { useLocation } from 'react-router-dom';

type AlertVariant = 'danger' | 'warning';

interface AlertConfig {
  variant: AlertVariant;
  title: string;
  message: string;
  buttonText: string;
  buttonLink: string;
}

/**
 * Shared component that shows subscription alert at the top of all pages
 * when subscription is missing, suspended, or canceled
 */
const SubscriptionAlert: React.FC = () => {
  const { status, loading } = useSubscription();
  const { t } = useTranslation();
  const location = useLocation();
  
  // Don't show alert on subscription pages (user is already there)
  const isSubscriptionPage = location.pathname === '/subscription' || location.pathname === '/renew-subscription';
  if (isSubscriptionPage) {
    return null;
  }

  // Don't show alert while loading or if subscription is active/trial
  if (loading || status === 'active' || status === 'trial') {
    return null;
  }

  // Show alert for no subscription, suspended, or canceled
  if (!status || status === 'error' || status === 'suspended' || status === 'canceled') {
    const getAlertMessage = (): AlertConfig | null => {
      if (!status || status === 'error') {
        return {
          variant: 'danger',
          title: t("header.No Subscription") || "No Active Subscription",
          message: t("header.You don't have an active subscription. Please subscribe to continue using our services.") || 
                   "You don't have an active subscription. Please subscribe to continue using our services.",
          buttonText: t("header.Subscribe Now") || "Subscribe Now",
          buttonLink: '/subscription'
        };
      } else if (status === 'suspended') {
        return {
          variant: 'warning',
          title: t("header.Subscription Suspended") || "Subscription Suspended",
          message: t("header.Your subscription has been suspended. Please renew to continue using our services.") || 
                   "Your subscription has been suspended. Please renew to continue using our services.",
          buttonText: t("header.Renew Subscription") || "Renew Subscription",
          buttonLink: '/renew-subscription'
        };
      } else if (status === 'canceled') {
        return {
          variant: 'danger',
          title: t("header.Subscription Stopped") || "Subscription Stopped",
          message: t("header.Your subscription has been stopped. Please renew to continue using our services.") || 
                   "Your subscription has been stopped. Please renew to continue using our services.",
          buttonText: t("header.Renew Subscription") || "Renew Subscription",
          buttonLink: '/renew-subscription'
        };
      }
      return null;
    };

    const alertConfig = getAlertMessage();
    if (!alertConfig) return null;

    return (
      <Alert 
        variant={alertConfig.variant} 
        className="mb-0 rounded-0"
        style={{ 
          position: 'sticky',
          top: 0,
          zIndex: 1050,
          borderRadius: 0
        }}
      >
        <div className="d-flex justify-content-between align-items-center flex-wrap">
          <div className="flex-grow-1 mb-2 mb-md-0">
            <Alert.Heading className="mb-2" style={{ fontSize: '1.1rem' }}>
              {alertConfig.title}
            </Alert.Heading>
            <p className="mb-0" style={{ fontSize: '0.95rem' }}>
              {alertConfig.message}
            </p>
          </div>
          <div className="ms-md-3">
            <button
              className={`btn btn-${alertConfig.variant}`}
              onClick={() => { window.location.href = alertConfig.buttonLink; }}
              style={{ 
                padding: '8px 24px', 
                fontSize: '0.95rem',
                whiteSpace: 'nowrap'
              }}
            >
              {alertConfig.buttonText}
            </button>
          </div>
        </div>
      </Alert>
    );
  }

  return null;
};

export default SubscriptionAlert;

